import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useRealtime } from "./RealtimeProvider";
import { usePermissions } from "../Auth/PermissionsContext";
import { UserAvatar } from "../Components/UserAvatar";

/**
 * "Who else is on this page right now." Filters live presence to the admins whose open tabs are on the
 * SAME path (minus the current user) and shows their avatar + name, linking to each profile. Renders
 * nothing when nobody else is here. Complements the edit-lock banner: the lock says who is EDITING;
 * this says who is simply VIEWING the same content alongside you.
 */

export const EditorPresence: React.FC<{ className?: string }> = ({ className }) => {
	const { users } = useRealtime();
	const { isMe } = usePermissions();
	const location = useLocation();

	const here = users.filter(
		(u) => (u.pages ?? []).includes(location.pathname) && !isMe(u.user) && !isMe(u.email) && !isMe(u.userId)
	);
	if (here.length === 0) return null;

	return (
		<div className={`flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 ${className ?? ""}`}>
			<span className="text-xs font-semibold text-amber-800">Aussi sur cette page :</span>
			<div className="flex -space-x-2">
				{here.slice(0, 6).map((u, i) => {
					const avatar = (
						<UserAvatar
							name={u.user}
							id={u.userId ?? undefined}
							avatarKey={u.avatarKey}
							pictureUrl={u.pictureUrl}
							sizeClass="h-6 w-6"
							textClass="text-[9px]"
							ringClass="ring-2 ring-amber-50"
						/>
					);
					return u.userId ? (
						<Link key={`${u.userId}-${i}`} to={`/account/${u.userId}`} aria-label={`Profil de ${u.user}`} className="transition hover:z-10 hover:opacity-90">
							{avatar}
						</Link>
					) : (
						<React.Fragment key={`${u.user}-${i}`}>{avatar}</React.Fragment>
					);
				})}
			</div>
			<span className="text-xs text-amber-700">{here.map((u) => u.user).join(", ")}</span>
		</div>
	);
};

export default EditorPresence;
